import type { SpecCatalogEntry } from '../../../services/integrationApi';

/**
 * Per-product spec values for the Spec metafields preview. A port of the
 * backend's `collectNormalizedSpecEntries` (src/utils/specExportFormatting.ts)
 * so a label here is the label the picker and the sync use. The full product
 * (`GET /products/:id`) carries the nested `specifications` object; the spec
 * catalog the picker reads only has one sample value per label.
 */

export interface ProductSpecEntry {
  label: string;
  value: string;
  /** Canonical `specifications` path (`lens.mount`); absent for flat top-level specs. */
  path?: string;
}

const escapeRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const stripFootnotes = (s: string): string => s.replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹]+/g, '');

function collapseSpacedCaps(label: string): string {
  let s = label;
  let prev = '';
  for (let i = 0; i < 8 && s !== prev; i++) {
    prev = s;
    s = s.replace(/\b(?:[A-Z]\s){2,}[A-Z]\b/g, (chunk) => chunk.replace(/\s/g, ''));
  }
  return s;
}

function formatKey(input: string): string {
  return String(input || '')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (ch) => ch.toUpperCase());
}

function normalizeLabel(section: string, rawLabel: string): string {
  const sec = section.trim();
  let label = stripFootnotes(rawLabel.trim());
  if (!label) return label;
  const secLower = sec.toLowerCase();
  for (let i = 0; i < 6; i++) {
    const dash = label.indexOf(' - ');
    if (dash <= 0) break;
    const left = label.slice(0, dash).trim().toLowerCase();
    if (left === secLower || left.startsWith(`${secLower} `)) {
      label = label.slice(dash + 3).trim();
      continue;
    }
    break;
  }
  label = label.replace(new RegExp(`^${escapeRegex(sec)}\\s*-\\s*`, 'i'), '').trim();
  const dupWord = new RegExp(`^${escapeRegex(sec)}\\s+`, 'i');
  if (dupWord.test(label)) label = label.replace(dupWord, '').trim();
  return stripFootnotes(collapseSpacedCaps(label)).trim();
}

function cleanupValue(raw: string): string {
  let s = String(raw ?? '').trim();
  if (!s) return s;
  s = s.replace(/\(\s+Approx\.\s*\)/gi, '(Approx.)').replace(/\(\s*Approx\.\s*\)\s+\d+\s*$/i, '(Approx.)');
  s = s.replace(/\b(Yes|No)(\d)\b/gi, '$1');
  s = s
    .replace(/([a-zA-Z])(\d)/g, '$1 $2')
    .replace(/(\d)([a-zA-Z])/g, '$1 $2')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([,;])(?=[A-Za-z])/g, '$1 ')
    .replace(/([^\s\d%])(\()/g, '$1 $2')
    .replace(/\)([A-Za-z])/g, ') $1')
    .replace(/(\))(\()/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  return stripFootnotes(s);
}

const formatValue = (input: unknown): string => cleanupValue(Array.isArray(input) ? input.join(', ') : String(input ?? ''));

export function specEntriesFromSpecifications(specifications: unknown): ProductSpecEntry[] {
  if (!specifications || typeof specifications !== 'object') return [];
  const entries: ProductSpecEntry[] = [];
  for (const [sectionName, sectionValue] of Object.entries(specifications as Record<string, unknown>)) {
    if (sectionName === 'raw' || sectionName === '_meta') continue;
    const section = formatKey(sectionName);
    if (sectionValue && typeof sectionValue === 'object' && !Array.isArray(sectionValue)) {
      for (const [fieldKey, rawValue] of Object.entries(sectionValue as Record<string, unknown>)) {
        if (rawValue === undefined || rawValue === null || rawValue === '') continue;
        const value = formatValue(rawValue);
        if (!value.trim()) continue;
        entries.push({
          label: normalizeLabel(section, formatKey(fieldKey)),
          value,
          path: `${sectionName}.${fieldKey}`,
        });
      }
    } else if (sectionValue !== undefined && sectionValue !== null && sectionValue !== '') {
      const value = formatValue(sectionValue);
      if (!value.trim()) continue;
      entries.push({ label: normalizeLabel('General', section), value });
    }
  }
  return entries;
}

/** The product's value for a catalog spec: by canonical path when it has one, else by label. */
export function productValueForSpec(
  entries: ProductSpecEntry[],
  spec: Pick<SpecCatalogEntry, 'label' | 'specPath'>,
): string {
  if (spec.specPath) {
    const byPath = entries.find((entry) => entry.path === spec.specPath);
    if (byPath) return byPath.value;
  }
  const label = spec.label.trim().toLowerCase();
  return entries.find((entry) => entry.label.trim().toLowerCase() === label)?.value ?? '';
}

/** Definition types a spec can never fill: other Shopify objects and rich text (the backend does not write it). */
export function isCantFillType(type: string): boolean {
  return /_reference$/.test(type) || type === 'rich_text_field';
}

export function cantFillReason(type: string): string {
  if (type === 'rich_text_field') return 'Rich text is not written from a spec. Edit it in Shopify.';
  if (/product_reference$/.test(type) || /variant_reference$/.test(type) || /collection_reference$/.test(type)) {
    return 'Links to other Shopify products. Set it in Shopify or with a recommendations app.';
  }
  if (/file_reference$/.test(type)) return 'Links to an uploaded file.';
  if (/metaobject_reference$/.test(type) || /mixed_reference$/.test(type)) {
    return 'Links to a metaobject entry, which has its own fields.';
  }
  return 'Links to another Shopify object.';
}
